import { useState, useEffect, useCallback } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  CircularProgress,
  Typography,
  Alert,
  SelectChangeEvent,
  OutlinedInput
} from '@mui/material';
import { LibraryBooks } from '@mui/icons-material';
import { logger } from '../utils/logger';
import { useCollections } from '../hooks/useCollections';
import { getCatalogDisplayName } from '../utils/stringHelpers';
import { ItemCollectionInfo } from '../types/collection';

interface CollectionMembershipSelectorProps {
  itemId: string | undefined;
  disabled?: boolean;
  onChange?: (collectionIds: string[]) => void;
}

export const CollectionMembershipSelector = ({
  itemId,
  disabled = false,
  onChange
}: CollectionMembershipSelectorProps) => {
  // Albums for the catalog the object belongs to (active catalog), not the
  // caller's own albums when viewing a delegated catalog.
  const { activeCatalogCollections, addItemToCollection, removeItemFromCollection, fetchItemCollections } = useCollections();
  const collections = activeCatalogCollections ?? [];
  const [selectedCollectionIds, setSelectedCollectionIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Load current collection membership when itemId is provided
  useEffect(() => {
    if (!itemId) {
      setIsLoading(false);
      setIsInitialized(true);
      return;
    }

    const loadMembership = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const membership: ItemCollectionInfo[] = await fetchItemCollections(itemId);
        const collectionIds = membership.map(m => m.collectionId);
        setSelectedCollectionIds(collectionIds);
        // Notify parent of initial state for completion tracking
        onChange?.(collectionIds);
      } catch (err) {
        logger.error('Failed to load collection membership', err);
        // Don't show error for initial load - might just be a new item
        // Still notify parent with empty array so completion tracking initializes
        onChange?.([]);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    loadMembership();
    // Note: onChange is intentionally excluded from deps - it's for notifying parent of changes,
    // not for triggering data fetches. Data should only refetch when itemId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, fetchItemCollections]);

  const handleChange = useCallback(async (event: SelectChangeEvent<string[]>) => {
    if (!itemId) return;

    const newSelectedIds = event.target.value as string[];
    const previousIds = selectedCollectionIds;

    // Find collections to add (in new but not in previous)
    const toAdd = newSelectedIds.filter(id => !previousIds.includes(id));

    // Find collections to remove (in previous but not in new)
    const toRemove = previousIds.filter(id => !newSelectedIds.includes(id));

    setIsSaving(true);
    setError(null);

    try {
      // Add new memberships
      await Promise.all(
        toAdd.map(collectionId => addItemToCollection(collectionId, itemId, undefined))
      );

      // Remove old memberships
      await Promise.all(
        toRemove.map(collectionId => removeItemFromCollection(collectionId, itemId))
      );

      setSelectedCollectionIds(newSelectedIds);
      onChange?.(newSelectedIds);
    } catch (err) {
      setError('Failed to update album membership');
      logger.error('Failed to update collection membership', err);
    } finally {
      setIsSaving(false);
    }
  }, [itemId, selectedCollectionIds, addItemToCollection, removeItemFromCollection, onChange]);

  const handleDelete = useCallback(async (collectionIdToRemove: string) => {
    if (!itemId) return;

    setIsSaving(true);
    setError(null);

    try {
      await removeItemFromCollection(collectionIdToRemove, itemId);
      const newSelectedIds = selectedCollectionIds.filter(id => id !== collectionIdToRemove);
      setSelectedCollectionIds(newSelectedIds);
      onChange?.(newSelectedIds);
    } catch (err) {
      setError('Failed to remove from album');
      logger.error('Failed to remove from collection', err);
    } finally {
      setIsSaving(false);
    }
  }, [itemId, selectedCollectionIds, removeItemFromCollection, onChange]);

  const isDisabled = disabled || !itemId;

  // Show loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading albums...
        </Typography>
      </Box>
    );
  }

  // Show empty state if no collections available
  if (isInitialized && collections.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        No albums available. Create an album first to organize your artworks.
      </Alert>
    );
  }

  return (
    <Box>
      <FormControl fullWidth disabled={isDisabled}>
        <InputLabel id="collection-membership-label">
          Select Albums
        </InputLabel>
        <Select
          labelId="collection-membership-label"
          multiple
          value={selectedCollectionIds}
          onChange={handleChange}
          input={<OutlinedInput label="Select Albums" />}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as string[]).map((collectionId) => {
                const collection = collections.find(c => c.collectionId === collectionId);
                return (
                  <Chip
                    key={collectionId}
                    label={getCatalogDisplayName(collection?.name || 'Unknown')}
                    icon={<LibraryBooks fontSize="small" />}
                    size="small"
                    onDelete={() => handleDelete(collectionId)}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                );
              })}
            </Box>
          )}
        >
          {collections.map((collection) => (
            <MenuItem key={collection.collectionId} value={collection.collectionId}>
              {getCatalogDisplayName(collection.name)} ({collection.itemCount} items)
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {!itemId && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Save artwork first to add to albums
        </Typography>
      )}

      {isSaving && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Saving...
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};
